import dynamic from 'next/dynamic';
import React from 'react';

import { JobPosition, JobPositionForCandidateDto } from '@pflegia/data-access';
import { CardStatus, CardType } from '@pflegia/types/common/job-position/card';
import LoadingModal from '@pflegia/ui/shared/dialog/LoadingModal';
import { useMatchDialogueStore } from '@pflegia/ui/stores/match-dialogue.store';

import JobListEmptyWithModal from '@domains/job-board/components/job-position-list/JobListEmptyWithModal';
import { JobPositionCardTracker } from '@domains/job-board/components/wrappers/job-position-card-tracker';
import { useJobPositionsFeatures } from '@domains/job-board/hooks/use-job-positions-features';

import WrappedSuggestionsModal from '@shared/components/dialogue/suggestions-modal/WrappedModal';
import SlimPositionCard from '@shared/components/slim-position-card/SlimPositionCard';

type SlimJobPosition = Pick<
  JobPosition,
  | 'employer'
  | 'fullTimeHours'
  | 'geoLocation'
  | 'headline'
  | 'id'
  | 'salaryHigh'
  | 'salaryLow'
  | 'showSalary'
  | 'title'
  | 'slug'
  | 'createdAt'
>;

const OffersDialogueModal = dynamic(() => import('@components/dialogue/OffersDialogueModal'), { loading: () => <LoadingModal /> });

interface JobGoneSuggestionsProps {
  suggestedJobs: JobPositionForCandidateDto[];
}

/**
 * JobGoneSuggestions Component
 *
 * A simplified job list component specifically for the 410 Gone page.
 * Shows slimmed job position cards without pagination.
 * Receives job data from the server instead of fetching on the client.
 */
const JobGoneSuggestions: React.FC<JobGoneSuggestionsProps> = ({ suggestedJobs }) => {
  const { onOpen: openDialog } = useMatchDialogueStore();
  const { open: isMatchModalVisible } = useMatchDialogueStore();
  const {
    featureByJobPositionId,
    loading: featuresLoading,
    isCompleted,
  } = useJobPositionsFeatures(suggestedJobs.map(({ jobPosition }: JobPositionForCandidateDto) => jobPosition.id) ?? []);

  if (!suggestedJobs.length) {
    return <JobListEmptyWithModal />;
  }

  return (
    <div className='mt-6 flex h-full flex-col gap-6 lg:mt-8'>
      <div className='flex flex-col gap-6'>
        {suggestedJobs.map(({ jobPosition, match }: JobPositionForCandidateDto) => {
          const features = { ...(featureByJobPositionId?.[jobPosition.id] || {}), loading: featuresLoading };

          return (
            <JobPositionCardTracker
              key={jobPosition.id}
              jobPosition={jobPosition}
              featuresBadges={features}
              isCompleted={isCompleted}
              slimmedJobCard>
              <SlimPositionCard
                jobPosition={jobPosition as unknown as SlimJobPosition}
                openDialog={openDialog}
                status={match ? (match.connectionStatus as CardStatus) : undefined}
                type={match ? (match.connectionType as CardType) : undefined}
                featuresBadges={features}
              />
            </JobPositionCardTracker>
          );
        })}
        {isMatchModalVisible && <OffersDialogueModal />}
        <WrappedSuggestionsModal enableOnExit={false} />
      </div>
    </div>
  );
};

export default JobGoneSuggestions;
